// Logs.js - Registos de atividade do sistema (administradores)
//
// Junta num só ecrã o que antes não existia em lado nenhum: as ações
// feitas na app, os eventos que os aparelhos reportam por si (programa
// terminado, falta sal, ficou inacessível) e os eventos do sistema.
//
// Os logs de autenticação continuam a vir do endpoint próprio deles
// (/auth/logs) e aparecem aqui como mais um separador, em vez de terem um
// cartão só para si no menu.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, RefreshCw, ClipboardList, Zap, Cpu, AlertTriangle,
  XCircle, Shield, User, Clock
} from 'lucide-react';
import './Logs.css';

const tabsDe = (t) => [
  { id: 'all', label: t('registos.tudo') },
  { id: 'action', label: t('registos.acoes') },
  { id: 'event', label: t('registos.eventos') },
  { id: 'system', label: t('registos.sistema') },
  { id: 'auth', label: t('registos.autenticacao') },
];

const periodosDe = (t) => [
  { hours: 24, label: t('registos.periodo24h') },
  { hours: 168, label: t('registos.periodo7d') },
  { hours: 720, label: t('registos.periodo30d') },
];

const metaDe = (t) => ({
  action: { label: t('registos.acao'), icon: Zap },
  event: { label: t('registos.evento'), icon: Cpu },
  system: { label: t('registos.sistema'), icon: ClipboardList },
});

function Logs({ apiUrl, onBack, token }) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState('all');
  const [hours, setHours] = useState(24);
  const [logs, setLogs] = useState([]);
  const [authLogs, setAuthLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'auth') {
        // Endpoint separado: os logs de autenticação têm outra forma
        // (IP, user-agent) e vivem noutra coleção
        const days = Math.max(1, Math.round(hours / 24));
        const res = await fetch(`${apiUrl}/auth/logs?limit=100&days=${days}`, {
          headers: authHeaders(),
        });
        if (res.status === 403) {
          setError(t('registos.acessoNegado'));
          return;
        }
        const data = await res.json();
        setAuthLogs(data.logs || []);
      } else {
        const params = new URLSearchParams({ hours: String(hours), limit: '200' });
        if (tab !== 'all') params.append('kind', tab);

        const [logsRes, sumRes] = await Promise.all([
          fetch(`${apiUrl}/logs?${params}`, { headers: authHeaders() }),
          fetch(`${apiUrl}/logs/summary?hours=${hours}`, { headers: authHeaders() }),
        ]);

        const data = await logsRes.json();
        if (!logsRes.ok) {
          setError(data.detail || t('registos.erroCarregar'));
          return;
        }
        setLogs(data.logs || []);
        if (sumRes.ok) setSummary(await sumRes.json());
      }
    } catch (err) {
      setError(t('registos.erroCarregar'));
    } finally {
      setLoading(false);
    }
  }, [apiUrl, authHeaders, tab, hours, t]);

  useEffect(() => { load(); }, [load]);

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString(i18n.language === 'en' ? 'en-GB' : 'pt-PT', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  return (
    <div className="logs-view">
      <div className="logs-header">
        <button onClick={onBack} className="logs-back">
          <ArrowLeft size={22} />
        </button>
        <div>
          <h1><ClipboardList size={26} /> {t('registos.titulo')}</h1>
          <p>{t('registos.subtitulo')}</p>
        </div>
        <button onClick={load} className="logs-refresh" disabled={loading}>
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      {/* Resumo */}
      {summary && tab !== 'auth' && (
        <div className="logs-summary">
          <div className="logs-stat">
            <Zap size={18} />
            <div>
              <strong>{summary.actions}</strong>
              <span>{t('registos.acoes')}</span>
            </div>
          </div>
          <div className="logs-stat">
            <Cpu size={18} />
            <div>
              <strong>{summary.events}</strong>
              <span>{t('registos.eventos')}</span>
            </div>
          </div>
          <div className={`logs-stat ${summary.warnings ? 'warn' : ''}`}>
            <AlertTriangle size={18} />
            <div>
              <strong>{summary.warnings}</strong>
              <span>{t('registos.avisos')}</span>
            </div>
          </div>
          <div className={`logs-stat ${summary.errors ? 'err' : ''}`}>
            <XCircle size={18} />
            <div>
              <strong>{summary.errors}</strong>
              <span>{t('registos.erros')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="logs-controls">
        <div className="logs-tabs">
          {tabsDe(t).map(tab_ => (
            <button
              key={tab_.id}
              className={`logs-tab ${tab === tab_.id ? 'active' : ''}`}
              onClick={() => setTab(tab_.id)}
            >
              {tab_.label}
            </button>
          ))}
        </div>
        <div className="logs-periods">
          {periodosDe(t).map(p => (
            <button
              key={p.hours}
              className={`logs-period ${hours === p.hours ? 'active' : ''}`}
              onClick={() => setHours(p.hours)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="logs-error">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="logs-empty">
          <RefreshCw size={32} className="spinning" />
          <p>{t('registos.aCarregar')}</p>
        </div>
      ) : tab === 'auth' ? (
        authLogs.length === 0 ? (
          <div className="logs-empty">
            <Shield size={32} />
            <p>{t('registos.semAuth')}</p>
          </div>
        ) : (
          <div className="logs-list">
            {authLogs.map((l, i) => (
              <div key={i} className="logs-row">
                <span className="logs-time"><Clock size={13} /> {formatTime(l.timestamp)}</span>
                <span className="logs-device"><User size={13} /> {l.username || 'desconhecido'}</span>
                <span className="logs-action">{l.event_type || l.event}</span>
                <span className="logs-user">{l.ip_address || ''}</span>
              </div>
            ))}
          </div>
        )
      ) : logs.length === 0 ? (
        <div className="logs-empty">
          <ClipboardList size={32} />
          <p>{t('registos.semRegistos')}</p>
          <span>{t('registos.semRegistosDica')}</span>
        </div>
      ) : (
        <div className="logs-list">
          {logs.map((l, i) => {
            const metas = metaDe(t); const meta = metas[l.kind] || metas.action;
            const Icon = meta.icon;
            return (
              <div key={i} className={`logs-row level-${l.level}`}>
                <span className="logs-time"><Clock size={13} /> {formatTime(l.timestamp)}</span>
                <span className={`logs-kind kind-${l.kind}`} title={meta.label}>
                  <Icon size={13} /> {meta.label}
                </span>
                <span className="logs-device">{l.device_name}</span>
                <span className="logs-action">{l.action}</span>
                <span className="logs-user">{l.user || ''}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Logs;
